import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    IconButton,
    Grid,
    TextField,
    Typography,
    InputAdornment,
    FormControlLabel,
    Switch,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { getAuth } from "firebase/auth";
import { db, storage } from "../../firebase";
import { getDoc, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

import imageCompression from "browser-image-compression";

import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";

import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

function VideoEdit() {
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const [user, setUser] = useState("");

    const [param, setParam] = useState("");

    const [imgUrl, setImgUrl] = useState("");
    const [imgOverwrap, setImgOverwrap] = useState("");
    const [imgDeleted, setImgDeleted] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [complete, setComplete] = useState(false);
    const [episode, setEpisode] = useState(1);
    const [url, setUrl] = useState("");
    const [release, setRelease] = useState(false);

    const fileInputRef = useRef(null);
    const handleAvaterOpen = () => {
        fileInputRef.current.value = "";
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const fileSrc = event.target.files[0];
        const fileURL = event.target.files;
        if (fileURL) {
            const supportedImageTypes = ["image/jpeg", "image/jpg", "image/png"];
            if (!supportedImageTypes.includes(fileSrc.type)) {
                console.log("Error: サポートされていない拡張子です");
                return;
            } else {
                const options = {
                    maxSizeMB: 2,
                    maxWidthOrHeight: 1280,
                    useWebWorker: true,
                    fileType: fileURL.type,
                    quality: 0.85,
                };

                const compressedFile = await imageCompression(fileSrc, options);
                const reader = new FileReader();
                reader.readAsDataURL(compressedFile);
                reader.onloadend = () => {
                    setImgUrl(reader.result);
                    setImgDeleted(false);
                };
            }
        }
    };

    const handleDeleteImage = () => {
        setImgUrl("");
        setImgDeleted(true);
    };

    const handleTitleChange = (event) => {
        setTitle(event.target.value);
    };

    const handleDescriptionChange = (event) => {
        setDescription(event.target.value);
    };

    const handleEpisodeChange = (event) => {
        setEpisode(event.target.value);
    };

    const handleCompleteChange = () => {
        setComplete(!complete);
    };

    const handleUrlChange = (event) => {
        setUrl(event.target.value);
    };

    const handleReleaseChange = () => {
        setRelease(!release);
    };

    const handleUpdateClick = async () => {
        try {
            // 画像が削除された場合
            if (imgDeleted) {
                // Storageの画像を削除
                if (imgOverwrap) {
                    const storageRef = ref(storage, `video/${param}`);
                    try {
                        await deleteObject(storageRef);
                    } catch (error) {
                        console.error("Error deleting storage image: ", error);
                    }
                }

                // Firestoreのデータを更新
                const videoRef = doc(db, "video", param);
                await updateDoc(videoRef, {
                    img: "",
                    title: title,
                    description: description,
                    complete: complete,
                    episode: episode,
                    url: url,
                    release: release,
                    auther: user,
                    updateAt: serverTimestamp(),
                });
            }
            // 新しい画像がアップロードされた場合
            else if (imgUrl !== imgOverwrap) {
                const storageRef = ref(storage, `video/${param}`);
                const blobFile = await fetch(imgUrl).then((res) => res.blob());

                await uploadBytes(storageRef, blobFile);
                const newImgUrl = await getDownloadURL(storageRef);

                const videoRef = doc(db, "video", param);
                await updateDoc(videoRef, {
                    img: newImgUrl,
                    title: title,
                    description: description,
                    complete: complete,
                    episode: episode,
                    url: url,
                    release: release,
                    auther: user,
                    updateAt: serverTimestamp(),
                });
            }
            // 画像に変更がない場合
            else {
                const videoRef = doc(db, "video", param);
                await updateDoc(videoRef, {
                    title: title,
                    description: description,
                    complete: complete,
                    episode: episode,
                    url: url,
                    release: release,
                    auther: user,
                    updateAt: serverTimestamp(),
                });
            }

            setSaveOpen(true);
        } catch (error) {
            console.error("Error updating IMG: ", error);
        }
    };

    const [saveOpen, setSaveOpen] = useState(false);
    const handleSaved = () => {
        window.scrollTo(0, 0);
        navigate("/video", { replace: true });
    };

    const [loadButton, setLoadButton] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const handleDeleteClose = () => {
        setDeleteOpen(false);
    };

    const handleDelete = () => {
        setDeleteOpen(true);
    };

    const handleDeleteComp = async () => {
        setLoadButton(true);

        const storageRef = ref(storage, `video/${param}`);
        const videoRef = doc(db, "video", param);
        try {
            await deleteObject(storageRef);

            await updateDoc(videoRef, {
                deleted: true,
                img: "",
                updateAt: serverTimestamp(),
                deleteAt: serverTimestamp(),
            });
        } catch (error) {
            console.error("Error deleting storage image: ", error);
        }

        setLoadButton(false);
        setDeleteCompOpen(true);
    };

    const [deleteCompOpen, setDeleteCompOpen] = useState(false);
    const handleDeleteComped = () => {
        window.scrollTo(0, 0);
        navigate("/scinario", { replace: true });
    };

    const fetchData = async () => {
        const params = window.location.pathname.split("/")[2];
        setParam(params);

        const videoRef = doc(db, "video", params);
        const videoSnap = await getDoc(videoRef);

        if (videoSnap.exists()) {
            setImgUrl(videoSnap.data().img);
            setImgOverwrap(videoSnap.data().img);
            setTitle(videoSnap.data().title);
            setDescription(videoSnap.data().description);
            setComplete(videoSnap.data().complete);
            setEpisode(videoSnap.data().episode);
            setUrl(videoSnap.data().url);
            setRelease(videoSnap.data().release);
        }

        setLoading(false);
    };

    useEffect(() => {
        const unsubscribe = getAuth().onAuthStateChanged((userLogin) => {
            if (userLogin) {
                const uid = userLogin.uid;
                setUser(uid);
                fetchData();
            } else {
                window.scrollTo(0, 0);
                navigate("/login", { replace: true });
            }
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <Box
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div>
            <Helmet>
                <title>YouTubeLink Create - SOG Member Site</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Container maxWidth="1280" sx={{ display: { xs: { maxWidth: 0 } }, maxWidth: 1280 }}>
                <Container>
                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ mt: 10 }}
                    >
                        <Typography variant="h6">YouTubeリンク作成</Typography>
                    </Box>

                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ m: 3 }}
                    >
                        <Box
                            sx={{
                                minWidth: 350,
                                minHeight: 196.875,
                                border: "1px solid",
                                borderColor: "primary.main",
                                borderRadius: 1,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: "pointer",
                                objectFit: "contain",
                                position: "relative",
                            }}
                            onClick={handleAvaterOpen}
                        >
                            <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} ref={fileInputRef} />

                            {imgUrl ? (
                                <img
                                    src={imgUrl}
                                    alt="uploaded"
                                    style={{
                                        maxWidth: 348,
                                        maxHeight: 195.75,
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        color: "text.secondary",
                                    }}
                                    type="file"
                                    onChange={handleAvaterOpen}
                                >
                                    サムネイルをアップロード
                                </Box>
                            )}

                            {imgUrl && (
                                <IconButton
                                    sx={{
                                        position: "absolute",
                                        top: 10,
                                        right: 10,
                                        color: "white",
                                    }}
                                    style={{
                                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteImage();
                                    }}
                                >
                                    <ClearIcon />
                                </IconButton>
                            )}
                        </Box>
                    </Box>

                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ m: 3 }}
                    >
                        <TextField variant="filled" label="タイトル" value={title} onChange={handleTitleChange} sx={{ minWidth: 350 }} />
                    </Box>

                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ m: 3 }}
                    >
                        <TextField
                            variant="filled"
                            label="あらすじ"
                            value={description}
                            onChange={handleDescriptionChange}
                            multiline
                            rows={5}
                            sx={{ minWidth: 350 }}
                        />
                    </Box>

                    <Box sx={{ mt: 3 }} style={{ display: "flex", justifyContent: "center" }}>
                        <Grid container sx={{ maxWidth: 350, minWidth: 350 }}>
                            <Grid item xs={7} sm={7}>
                                <TextField
                                    sx={{
                                        input: { textAlign: "right" },
                                        "& .MuiInputBase-input": {
                                            display: "flex",
                                            height: "100%", // 入力値の高さを100%に設定
                                            transform: "translateY(-17%)", // 入力値を上に10%移動
                                        },
                                    }}
                                    value={episode}
                                    variant="filled"
                                    onChange={handleEpisodeChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment
                                                position="start"
                                                sx={{
                                                    height: "100%", // プレフィックスの高さを100%に設定
                                                    display: "flex",
                                                    transform: "translateY(-35%)", // プレフィックスを上に10%移動
                                                }}
                                            >
                                                全
                                            </InputAdornment>
                                        ),
                                        endAdornment: <InputAdornment position="end">話</InputAdornment>,
                                        type: "number",
                                    }}
                                    disabled={!complete}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={5}
                                sm={5}
                                style={{
                                    display: "flex",
                                    justifyContent: "right",
                                    alignItems: "center",
                                }}
                            >
                                <FormControlLabel
                                    checked={complete}
                                    onChange={handleCompleteChange}
                                    label="完結済み"
                                    labelPlacement="start"
                                    control={<Switch sx={{ ml: 2 }} />}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ mt: 3 }} style={{ display: "flex", justifyContent: "center" }}>
                        <Grid container sx={{ maxWidth: 350, minWidth: 350 }}>
                            <Grid item xs={7} sm={7}>
                                <TextField value={url} variant="filled" label="YouTube URL" onChange={handleUrlChange} />
                            </Grid>
                            <Grid
                                item
                                xs={5}
                                sm={5}
                                style={{
                                    display: "flex",
                                    justifyContent: "right",
                                    alignItems: "center",
                                }}
                            >
                                <FormControlLabel
                                    checked={release}
                                    onChange={handleReleaseChange}
                                    label="公開中"
                                    labelPlacement="start"
                                    control={<Switch sx={{ ml: 2 }} />}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ mt: 5 }}
                    >
                        <Button variant="contained" onClick={handleUpdateClick} size="large" sx={{ width: "350px" }}>
                            更新
                        </Button>
                    </Box>

                    <Box
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        sx={{ mt: 3, mb: 2 }}
                    >
                        <Button color="error" variant="contained" onClick={handleDelete} size="large" sx={{ width: "350px" }}>
                            削除
                        </Button>
                    </Box>
                </Container>
            </Container>

            <Dialog open={saveOpen}>
                <DialogTitle
                    sx={{
                        minWidth: 263,
                        justifyContent: "center",
                        display: "flex",
                    }}
                >
                    更新しました
                </DialogTitle>
                <DialogActions sx={{ justifyContent: "center", display: "flex", mb: 2 }}>
                    <Button variant="contained" sx={{ width: "100px" }} onClick={handleSaved}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteOpen} onClose={handleDeleteClose}>
                <DialogTitle sx={{ mb: 2, minWidth: 263 }}>
                    <IconButton
                        onClick={handleDeleteClose}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography style={{ display: "flex", justifyContent: "center" }}>削除しますか？</Typography>
                </DialogContent>
                <DialogActions>
                    <LoadingButton onClick={handleDeleteClose} variant="outlined" loading={loadButton} loadingIndicator="削除中">
                        キャンセル
                    </LoadingButton>
                    <LoadingButton onClick={handleDeleteComp} variant="contained" loading={loadButton}>
                        OK
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteCompOpen}>
                <DialogTitle
                    sx={{
                        minWidth: 263,
                        justifyContent: "center",
                        display: "flex",
                    }}
                >
                    YouTubeリンクを削除しました
                </DialogTitle>
                <DialogActions sx={{ justifyContent: "center", display: "flex", mb: 2 }}>
                    <Button variant="contained" sx={{ width: "100px" }} onClick={handleDeleteComped}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default VideoEdit;
